import { getIntl } from 'umi';
import dvaModelExtend from 'dva-model-extend';

import { bindServices } from '@/services/api';
import { createCrudModel } from '@/utils/core/dva';
import { CoreEnums } from '@/utils/domain/constants';
import { toPaginate } from '@/utils/infrastructure';

import {
  DESTINATION_ENTITY_NAME,
  DESTINATION_FETCH_ROUTE,
  DESTINATION_MODEL_NAME,
  MODEL_NAME as PARENT_MODEL,
} from '../constants';
import {
  filterByActive,
  filterByConnector,
  filterByName,
  filterByStatus,
  normalizeCollection,
  slicePage,
  sortRows,
} from '../derived';
import { notifyGlobal, reportWriteError } from '../effects';

const api = bindServices(DESTINATION_MODEL_NAME);

function* refetchList(put, select) {
  const latest = yield select((state) => state[DESTINATION_MODEL_NAME].latestFetchPayload);
  if (latest) yield put({ type: 'fetchList', payload: latest });
}

const _model = createCrudModel({
  namespace: DESTINATION_MODEL_NAME,
  fetchRoute: DESTINATION_FETCH_ROUTE,
  fetchParams: 'SecurityEventDestination',
  entityName: DESTINATION_ENTITY_NAME,
  api,
  features: { import: false, bulkActions: false },
  state: {
    searchTable: '',
    deleteConfirm: null,
    subscriptionId: null,
    latestFetchPayload: null,
    rowActionLoadingItemIds: {},
  },
  effects: {
    *fetchList({ payload }, { call, put, select }) {
      const subscriptionId = payload?.subscriptionId;
      const page = payload?.page ?? 1;
      const pageSize = yield select((state) => state.global.pageSize);
      const limit = payload?.limit ?? pageSize;
      try {
        yield put({
          type: 'queryListMeta',
          payload: { subscriptionId, latestFetchPayload: payload || null },
        });
        const response = yield call(api.getAll, {
          route: `security-events/subscriptions/${subscriptionId}/destinations`,
          readPriority: true,
        });
        const rows = normalizeCollection(response);
        yield put({
          type: `${PARENT_MODEL}/syncSubscriptionDestinations`,
          payload: { subscriptionId, rows },
        });
        const filtered = sortRows(
          filterByConnector(
            filterByStatus(
              filterByActive(filterByName(rows, payload?.search), payload?.activeFilter),
              payload?.statusFilter,
            ),
            payload?.connectorFilter,
          ),
          payload?.orderBy,
        );
        yield put({
          type: 'queryDestinationData',
          payload: {
            items: slicePage(filtered, page, limit).map((item) => ({
              ...item,
              isActive: item.isActive === true,
            })),
            totalCount: filtered.length,
            page,
            limit,
          },
        });
      } catch (e) {
        // Business failures use `status: 2` (Error) and `code: "400"`.
        // This GET only returns that when the subscription is gone.
        const httpStatus = Number(e?.response?.status);
        const directStatus = e?.code == null ? Number(e?.status) : NaN;
        const missingSubscription =
          httpStatus === 400 || directStatus === 400 || String(e?.code) === '400';
        if (missingSubscription) {
          yield put({
            type: `${PARENT_MODEL}/showDestinationsDrawer`,
            payload: { open: false, item: null },
          });
          yield put({ type: `${PARENT_MODEL}/repeatLatestFetch` });
        }
      }
    },

    *fetchCreate({ payload }, { put, call, select }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.create, { route: DESTINATION_FETCH_ROUTE, data: payload.data });
        yield put({ type: 'success', payload: { success: true } });
        yield* notifyGlobal(put, { type: 'success' });
        yield* refetchList(put, select);
      } catch (e) {
        yield* reportWriteError(e, put);
      }
    },

    *fetchUpdate({ payload }, { put, call, select }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        yield call(api.update, { route: DESTINATION_FETCH_ROUTE, data: payload.data });
        const id = payload.data?.id;
        let fresh = null;
        if (id) {
          fresh = yield call(api.getById, { route: DESTINATION_FETCH_ROUTE, id });
        }
        const incomplete = Number(fresh?.status) === CoreEnums.SiemDestinationStatus.Incomplete;
        if (incomplete) {
          yield* notifyGlobal(put, {
            type: 'warning',
            message: getIntl().formatMessage({ id: 'securityEvents.destination.retest' }),
          });
        } else {
          yield* notifyGlobal(put, { type: 'success' });
        }
        yield put({ type: 'success', payload: { success: true } });
        yield* refetchList(put, select);
      } catch (e) {
        yield* reportWriteError(e, put);
      }
    },

    *fetchDelete({ payload }, { call, put, select }) {
      try {
        const { id, view } = payload;
        yield call(api.remove, { route: DESTINATION_FETCH_ROUTE, id });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: { state: false, item: null, detail: false, id: null },
          });
        }
        yield put({ type: 'queryDeleteConfirm', payload: null });
        yield* notifyGlobal(put, { type: 'success' });
        yield* refetchList(put, select);
      } catch (e) {
        if (e?.message) yield* notifyGlobal(put, { type: 'error', message: e.message });
      } finally {
        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
      }
    },

    *testDestination({ payload }, { call, put, select }) {
      const id = payload?.id;
      yield put({ type: 'queryRowActionLoading', payload: { id, active: true } });
      try {
        const result = yield call(api.apiRoute, {
          route: `${DESTINATION_FETCH_ROUTE}/${payload.id}/test`,
          method: 'post',
        });
        if (result?.success) {
          yield* notifyGlobal(put, {
            type: 'success',
            ...(result.message ? { message: result.message } : {}),
          });
          const detail = yield select((state) => state[DESTINATION_MODEL_NAME]);
          if (detail.showForm && detail.detail && detail.id === payload.id) {
            const fresh = yield call(api.getById, {
              route: DESTINATION_FETCH_ROUTE,
              id: payload.id,
            });
            yield put({ type: 'queryItem', payload: fresh });
          }
          yield* refetchList(put, select);
          return;
        }
        const extra = [result?.errorCode, result?.httpStatus]
          .filter((value) => value != null && value !== '')
          .join(' · ');
        const message = extra ? `${result?.message || ''} (${extra})`.trim() : result?.message;
        yield* notifyGlobal(put, { type: 'error', message });
      } catch (e) {
        yield* reportWriteError(e, put);
      } finally {
        yield put({ type: 'queryRowActionLoading', payload: { id, active: false } });
      }
    },

    *fetchActivate({ payload }, { put, call }) {
      const { id } = payload;
      yield put({ type: 'queryActivatingItem', payload: { id, active: true } });
      try {
        yield call(api.activate, {
          route: DESTINATION_FETCH_ROUTE,
          id,
          isActive: payload.state,
        });
        yield put({ type: `${DESTINATION_ENTITY_NAME}Activate`, payload });
        yield put({
          type: `${PARENT_MODEL}/patchDestinationActive`,
          payload: { id, isActive: payload.state === true },
        });
      } catch (e) {
        /* activation errors surface through the shared request handler */
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { id, active: false } });
      }
    },

    *setSearchTable({ payload }, { put }) {
      yield put({ type: 'querySearchTable', payload });
    },

    *openDeleteConfirm({ payload }, { put }) {
      yield put({ type: 'queryDeleteConfirm', payload });
    },

    *closeDeleteConfirm(_, { put }) {
      yield put({ type: 'queryDeleteConfirm', payload: null });
    },

    *clearList(_, { put }) {
      yield put({ type: 'queryClearList' });
    },
  },
  reducers: {
    queryDestinationData(state, { payload }) {
      return {
        ...state,
        list: payload.items || [],
        pagination: { ...state.pagination, ...toPaginate(payload) },
      };
    },
    queryListMeta(state, { payload }) {
      return {
        ...state,
        subscriptionId: payload.subscriptionId ?? state.subscriptionId,
        latestFetchPayload: payload.latestFetchPayload,
      };
    },
    querySearchTable(state, { payload }) {
      return { ...state, searchTable: payload ?? '' };
    },
    queryDeleteConfirm(state, { payload }) {
      return { ...state, deleteConfirm: payload?.id ? payload : null };
    },
    queryClearList(state) {
      return { ...state, list: [], pagination: { page: 1, count: 0, totalPages: 0 } };
    },
    queryRowActionLoading(state, { payload }) {
      const rowActionLoadingItemIds = { ...(state.rowActionLoadingItemIds || {}) };
      const { id, active } = payload || {};
      if (!id) return state;
      if (active) rowActionLoadingItemIds[id] = true;
      else delete rowActionLoadingItemIds[id];
      return { ...state, rowActionLoadingItemIds };
    },
  },
});

export default dvaModelExtend(_model, { namespace: DESTINATION_MODEL_NAME });
